import {
	Timeline,
	TimelineConnector,
	TimelineContent,
	TimelineItem,
	TimelineSeparator,
	timelineItemClasses,
} from "@mui/lab";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Paper,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import {
	type Comment,
	deleteCommentById,
	updateComment,
} from "../../../api/comments/comments";
import { deleteEmailOnQueue } from "../../../api/messaging/email";
import { deleteLinkedinMessageOnQueue } from "../../../api/messaging/linkedin-message";
import {
	deletePersonActivity,
	type PersonActivity,
	updatePersonActivity,
} from "../../../api/person/activity";
import type { PersonEmailMessage } from "../../../api/person/emails";
import { getIsConnected } from "../../../api/person/person";
import { deleteAttachment } from "../../../api/person/person-attachment";
import type { PersonEmail } from "../../../api/personemail/person-email";
import type { PersonVote } from "../../../api/votes";
import { useAttachmentViewer } from "../../attachment-viewer/attachment-viewer";
import { EmailDebugDialog } from "../../chatbox/EmailDebugDialog";
import { KeyValueChip } from "../../common/KeyValueChip";
import { useGlobalState } from "../../globalState";
import { ActivityDebugDialog } from "../ActivityDebugDialog";
import { ActivityAddDialog } from "../activity-add-dialog";
import { EntryEditDialog } from "./entry-edit-dialog";
import { type ReplyContext, ReplyDialog } from "./reply-dialog";
import { TimelineEntryContent, TimelineEntryDot } from "./timeline-entries";
import {
	type ActivityEntry,
	type ActivityFilter,
	FILTER_OPTIONS,
} from "./timeline-types";
import { countEntriesByFilter, getEntryCategory } from "./timeline-utils";
import { useTimelineData } from "./use-timeline-data";
import { VoteEditDialog } from "./vote-edit-dialog";

interface PersonActivityTimelineProps {
	personId: number;
	onRefreshNeeded?: () => void;
	personEmails?: PersonEmail[];
	/** Pre-seed enabledFilters (e.g. ["notes"] to scope to a single category). */
	initialFilters?: ActivityFilter[];
}

const ALL_FILTERS: ActivityFilter[] = [
	"notes",
	"attachments",
	"emails",
	"linkedin",
	"calls",
	"meetings",
	"applications",
	"votes",
	"board",
	"cooldowns",
];

export const PersonActivityTimeline = ({
	personId,
	onRefreshNeeded,
	personEmails = [],
	initialFilters,
}: PersonActivityTimelineProps) => {
	const theme = useTheme();
	const { currentUser } = useGlobalState();

	const { entries, loading, error, refetch } = useTimelineData(personId);

	const [showDebugIds, setShowDebugIds] = useState(false);
	const [isConnected, setIsConnected] = useState(false);
	// Multi-select filters - all ON by default (excluding "all" which is special)
	const [enabledFilters, setEnabledFilters] = useState<Set<ActivityFilter>>(
		() =>
			new Set<ActivityFilter>(
				initialFilters && initialFilters.length > 0
					? initialFilters
					: ALL_FILTERS
			)
	);
	const [debugDialogOpen, setDebugDialogOpen] = useState(false);
	const [debugEmail, setDebugEmail] = useState<PersonEmailMessage | null>(null);
	const [genericDebugDialogOpen, setGenericDebugDialogOpen] = useState(false);
	const [genericDebugEntry, setGenericDebugEntry] = useState<ActivityEntry | null>(
		null
	);
	const [addDialogOpen, setAddDialogOpen] = useState(false);
	const [voteEditDialogOpen, setVoteEditDialogOpen] = useState(false);
	const [editingVote, setEditingVote] = useState<PersonVote | null>(null);
	const [editingActivity, setEditingActivity] = useState<PersonActivity | null>(null);
	const [editingNote, setEditingNote] = useState<Comment | null>(null);
	const [replyDialogOpen, setReplyDialogOpen] = useState(false);
	const [replyContext, setReplyContext] = useState<ReplyContext | null>(null);
	const { open: openAttachmentViewer, element: attachmentViewerElement } =
		useAttachmentViewer();

	// Toggle debug IDs with Ctrl-Alt-D, the chord every debug view uses.
	// Compare lowercased: without Shift the browser reports "d", so testing for
	// "D" would silently make Shift mandatory.
	const handleKeyDown = useCallback((e: KeyboardEvent) => {
		if (e.ctrlKey && e.altKey && e.key.toLowerCase() === "d") {
			e.preventDefault();
			setShowDebugIds((prev) => !prev);
		}
	}, []);

	const handleAttachmentDelete = useCallback(
		async (attachment: Parameters<typeof deleteAttachment>[0]) => {
			await deleteAttachment(attachment);
			refetch();
		},
		[refetch]
	);

	const handleEmailQueueDelete = useCallback(
		async (id: number) => {
			if (!window.confirm("Delete this queued email?")) return;
			await deleteEmailOnQueue(id);
			refetch();
		},
		[refetch]
	);

	const handleLinkedinQueueDelete = useCallback(
		async (id: number) => {
			if (!window.confirm("Delete this queued LinkedIn message?")) return;
			await deleteLinkedinMessageOnQueue(id);
			refetch();
		},
		[refetch]
	);

	const handleActivityDelete = useCallback(
		async (activityId: number) => {
			if (!window.confirm("Delete this activity?")) return;
			await deletePersonActivity(personId, activityId);
			refetch();
		},
		[personId, refetch]
	);

	const handleNoteDelete = useCallback(
		async (noteId: number) => {
			if (!window.confirm("Delete this note?")) return;
			await deleteCommentById(noteId);
			refetch();
		},
		[refetch]
	);

	useEffect(() => {
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [handleKeyDown]);

	// Fetch connection status on mount
	useEffect(() => {
		if (personId) {
			getIsConnected(personId)
				.then((res) => setIsConnected(res.is_connected))
				.catch(() => setIsConnected(false));
		}
	}, [personId]);

	if (loading) {
		return (
			<Box
				sx={{ display: "flex", justifyContent: "center", p: 4 }}
				data-component="communication-loading"
			>
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Alert severity="error" data-component="communication-error">
				{error}
			</Alert>
		);
	}

	// Filter entries based on enabled filters (multi-select)
	const filteredEntries = entries.filter((entry) => {
		const category = getEntryCategory(entry);
		return enabledFilters.has(category);
	});

	// Check if all filters are enabled
	const allFiltersEnabled = enabledFilters.size === ALL_FILTERS.length;

	// Toggle a filter on/off
	const toggleFilter = (filterId: ActivityFilter) => {
		if (filterId === "all") {
			if (allFiltersEnabled) {
				setEnabledFilters(new Set());
			} else {
				setEnabledFilters(new Set(ALL_FILTERS));
			}
		} else {
			setEnabledFilters((prev) => {
				const next = new Set(prev);
				if (next.has(filterId)) {
					next.delete(filterId);
				} else {
					next.add(filterId);
				}
				return next;
			});
		}
	};

	// Add button — shared by the empty and populated states (rendered on its own row).
	const addButton = (
		<Button
			variant="contained"
			onClick={() => setAddDialogOpen(true)}
			data-component="add-activity-button"
			sx={{ mb: 2 }}
		>
			Add Activity
		</Button>
	);

	if (entries.length === 0) {
		return (
			<>
				{addButton}
				<Paper sx={{ p: 2 }} data-component="activity-empty">
					<Typography variant="body2" color="text.secondary">
						No activity found for this person.
					</Typography>
				</Paper>
				<ActivityAddDialog
					open={addDialogOpen}
					onClose={() => setAddDialogOpen(false)}
					personId={personId}
					userId={currentUser.id}
					onSuccess={refetch}
					personEmails={personEmails}
					isFirstDegree={isConnected}
				/>
			</>
		);
	}

	const counts = countEntriesByFilter(entries);

	return (
		<>
			{/* Header: Filter Chips, then the Add button on its own row below */}
			<Stack
				direction="row"
				spacing={0.75}
				sx={{ flexWrap: "wrap", gap: 0.75, mb: 2 }}
				data-component="activity-filter-chips"
			>
				{FILTER_OPTIONS.map((filter) => (
					<KeyValueChip
						key={filter.id}
						label={filter.label}
						value={counts[filter.id]}
						selected={
							filter.id === "all"
								? allFiltersEnabled
								: enabledFilters.has(filter.id)
						}
						color={filter.color}
						onClick={() => toggleFilter(filter.id)}
					/>
				))}
			</Stack>
			{addButton}

			{filteredEntries.length === 0 ? (
				<Paper sx={{ p: 2 }} data-component="activity-filtered-empty">
					<Typography variant="body2" color="text.secondary">
						No activity matches the selected filters.
					</Typography>
				</Paper>
			) : (
				<Timeline
					sx={{
						p: 0,
						m: 0,
						[`& .${timelineItemClasses.root}:before`]: {
							flex: 0,
							padding: 0,
						},
					}}
					data-component="activity-timeline"
				>
					{filteredEntries.map((entry, idx) => (
						<TimelineItem
							key={`${entry.type}-${idx}`}
							data-component="timeline-entry"
						>
							<TimelineSeparator data-component="entry-separator">
								<TimelineEntryDot entry={entry} theme={theme} />
								{idx < filteredEntries.length - 1 && (
									<TimelineConnector
										sx={{ bgcolor: theme.palette.divider }}
										data-component="entry-connector"
									/>
								)}
							</TimelineSeparator>
							<TimelineContent
								sx={{ py: 0.5, pl: 1.5 }}
								data-component="entry-content-wrapper"
							>
								<TimelineEntryContent
									entry={entry}
									theme={theme}
									showDebugIds={showDebugIds}
									currentUsername={currentUser?.username}
									onDebugClick={(email) => {
										setDebugEmail(email);
										setDebugDialogOpen(true);
									}}
									onGenericDebugClick={(e) => {
										setGenericDebugEntry(e);
										setGenericDebugDialogOpen(true);
									}}
									onVoteEditClick={(vote) => {
										setEditingVote(vote);
										setVoteEditDialogOpen(true);
									}}
									onReplyClick={(context) => {
										setReplyContext(context);
										setReplyDialogOpen(true);
									}}
									onAttachmentDelete={handleAttachmentDelete}
									onAttachmentView={openAttachmentViewer}
									onEmailQueueDelete={handleEmailQueueDelete}
									onLinkedinQueueDelete={handleLinkedinQueueDelete}
									onActivityDelete={handleActivityDelete}
									onNoteDelete={handleNoteDelete}
									onActivityEdit={(activity) =>
										setEditingActivity(activity)
									}
									onNoteEdit={(note) => setEditingNote(note)}
									currentUserId={currentUser?.id}
									isAdmin={
										currentUser?.is_staff ||
										currentUser?.is_superuser
									}
								/>
							</TimelineContent>
						</TimelineItem>
					))}
				</Timeline>
			)}
			{attachmentViewerElement}
			<EmailDebugDialog
				open={debugDialogOpen}
				onClose={() => setDebugDialogOpen(false)}
				email={debugEmail}
				personId={personId}
			/>
			<ActivityDebugDialog
				open={genericDebugDialogOpen}
				onClose={() => setGenericDebugDialogOpen(false)}
				entry={genericDebugEntry}
				isStaff={Boolean(currentUser?.is_staff || currentUser?.is_superuser)}
			/>
			<ActivityAddDialog
				open={addDialogOpen}
				onClose={() => setAddDialogOpen(false)}
				personId={personId}
				userId={currentUser.id}
				onSuccess={refetch}
				personEmails={personEmails}
				isFirstDegree={isConnected}
			/>
			<VoteEditDialog
				open={voteEditDialogOpen}
				onClose={() => setVoteEditDialogOpen(false)}
				vote={editingVote}
				onUpdate={refetch}
			/>
			<EntryEditDialog
				open={editingActivity !== null}
				onClose={() => setEditingActivity(null)}
				title={
					editingActivity?.activity_type === "meeting"
						? "Edit Meeting"
						: "Edit Call"
				}
				initialText={editingActivity?.content ?? ""}
				onSave={async (text) => {
					if (!editingActivity) return;
					await updatePersonActivity(personId, editingActivity.id, {
						content: text,
					});
				}}
				onUpdate={refetch}
			/>
			<EntryEditDialog
				open={editingNote !== null}
				onClose={() => setEditingNote(null)}
				title="Edit Note"
				initialText={editingNote?.comment ?? ""}
				onSave={async (text) => {
					if (!editingNote) return;
					await updateComment("persons", personId, editingNote.id, text);
				}}
				onUpdate={refetch}
			/>
			<ReplyDialog
				open={replyDialogOpen}
				onClose={() => setReplyDialogOpen(false)}
				replyTo={replyContext}
				personId={personId}
				userId={currentUser.id}
				personEmails={personEmails}
				onSuccess={refetch}
			/>
		</>
	);
};

// Keep the old export name for backwards compatibility
export const PersonCommunicationTimeline = PersonActivityTimeline;
export default PersonActivityTimeline;

// Re-export types for consumers
export type { ActivityEntry, ActivityFilter } from "./timeline-types";
